import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Recycle, ArrowLeft, Building2, Plus, Trash2, Edit, Save, X, 
  Users, TrendingUp, FileText, Bell, Shield, UserCog, Crown,
  CheckCircle, XCircle, Clock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminAPI, organizationsAPI } from '../api/client';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';

const AdminPanel = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [loading, setLoading] = useState(true);

  // Organization form state
  const [orgForm, setOrgForm] = useState({
    name: '',
    description: '',
    website: '',
    contact_email: '',
    supported_waste_streams: [],
    rates: []
  });

  // Waste stream options
  const wasteStreamOptions = [
    { value: 'packaging', label: 'Pakuotės' },
    { value: 'weee', label: 'WEEE (Elektros įranga)' },
    { value: 'oil', label: 'Alyvos' },
    { value: 'textile', label: 'Tekstilė' }
  ];

  // Rate form state
  const [newRate, setNewRate] = useState({
    waste_stream: 'packaging',
    packaging_type: 'plastic',
    rate_per_kg: 0,
    discount_percentage: 0,
    year: new Date().getFullYear()
  });

  // Notification form state
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    notification_type: 'system_announcement',
    priority: 'medium',
    send_email: false
  });

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsResponse, orgsResponse, usersResponse] = await Promise.all([
        adminAPI.getStats(),
        organizationsAPI.getAll(),
        adminAPI.getAllUsers()
      ]);
      setStats(statsResponse.data);
      setOrganizations(orgsResponse.data);
      setUsers(usersResponse.data);
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko užkrauti duomenų",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await adminAPI.updateUserRole(userId, newRole);
      await loadData();
      toast({
        title: "Vartotojas atnaujintas",
        description: `Rolė pakeista į ${newRole === 'admin' ? 'Administratorius' : 'Vartotojas'}`
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: error.response?.data?.detail || "Nepavyko atnaujinti vartotojo",
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Ar tikrai norite ištrinti vartotoją ${userEmail}?`)) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      await loadData();
      toast({
        title: "Vartotojas ištrintas",
        description: "Vartotojas sėkmingai pašalintas"
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: error.response?.data?.detail || "Nepavyko ištrinti vartotojo",
        variant: "destructive"
      });
    }
  };

  const handleApproveUser = async (userId, userEmail) => {
    try {
      await adminAPI.approveUser(userId);
      await loadData();
      toast({
        title: "Vartotojas patvirtintas",
        description: `${userEmail} dabar gali prisijungti`
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: error.response?.data?.detail || "Nepavyko patvirtinti vartotojo",
        variant: "destructive"
      });
    }
  };

  const handleRejectUser = async (userId, userEmail) => {
    if (!window.confirm(`Atšaukti prieigą vartotojui ${userEmail}?`)) return;
    try {
      await adminAPI.rejectUser(userId);
      await loadData();
      toast({
        title: "Prieiga atmesta",
        description: `${userEmail} nebegalės prisijungti`
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: error.response?.data?.detail || "Klaida",
        variant: "destructive"
      });
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    
    try {
      await adminAPI.createNotification(notificationForm);
      
      toast({
        title: "Pranešimas sukurtas!",
        description: "Visi vartotojai matys šį pranešimą"
      });
      
      // Reset form
      setNotificationForm({
        title: '',
        message: '',
        notification_type: 'system_announcement',
        priority: 'medium',
        send_email: false
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko sukurti pranešimo",
        variant: "destructive"
      });
    }
  };

  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Ar tikrai norite ištrinti šią organizaciją?')) {
      return;
    }

    try {
      await adminAPI.deleteOrganization(orgId);
      await loadData();
      
      toast({
        title: "Organizacija ištrinta!",
        description: "Organizacija sėkmingai pašalinta"
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko ištrinti organizacijos",
        variant: "destructive"
      });
    }
  };

  const handleSaveOrganization = async (e) => {
    e.preventDefault();
    
    try {
      if (editingOrg) {
        await adminAPI.updateOrganization(editingOrg.id, orgForm);
        toast({
          title: "Organizacija atnaujinta!",
          description: "Pakeitimai išsaugoti sėkmingai"
        });
      } else {
        await adminAPI.createOrganization(orgForm);
        toast({
          title: "Organizacija sukurta!",
          description: "Nauja organizacija pridėta į sistemą"
        });
      }
      
      setEditingOrg(null);
      setShowAddOrg(false);
      setOrgForm({ name: '', description: '', website: '', contact_email: '', supported_waste_streams: [], rates: [] });
      await loadData();
    } catch (error) {
      toast({
        title: "Klaida",
        description: error.response?.data?.detail || "Nepavyko išsaugoti organizacijos",
        variant: "destructive"
      });
    }
  };

  const handleEditOrganization = (org) => {
    setEditingOrg(org);
    setOrgForm({
      name: org.name,
      description: org.description,
      website: org.website || '',
      contact_email: org.contact_email || '',
      supported_waste_streams: org.supported_waste_streams || [],
      rates: org.rates || []
    });
    setShowAddOrg(true);
  };

  const handleAddRate = () => {
    if (newRate.rate_per_kg <= 0 || newRate.discount_percentage < 0) {
      toast({
        title: "Klaida",
        description: "Įveskite teisingas reikšmes",
        variant: "destructive"
      });
      return;
    }

    const rate = {
      waste_stream: newRate.waste_stream,
      packaging_type: newRate.waste_stream === 'packaging' ? newRate.packaging_type : null,
      rate_per_kg: parseFloat(newRate.rate_per_kg),
      discount_percentage: parseFloat(newRate.discount_percentage),
      year: newRate.year ? parseInt(newRate.year) : null
    };

    setOrgForm({
      ...orgForm,
      rates: [...orgForm.rates, rate]
    });

    setNewRate({
      waste_stream: 'packaging',
      packaging_type: 'plastic',
      rate_per_kg: 0,
      discount_percentage: 0,
      year: new Date().getFullYear()
    });
  };

  const handleRemoveRate = (index) => {
    setOrgForm({
      ...orgForm,
      rates: orgForm.rates.filter((_, i) => i !== index)
    });
  };

  const toggleWasteStream = (stream) => {
    const current = orgForm.supported_waste_streams || [];
    if (current.includes(stream)) {
      setOrgForm({
        ...orgForm,
        supported_waste_streams: current.filter(s => s !== stream)
      });
    } else {
      setOrgForm({
        ...orgForm,
        supported_waste_streams: [...current, stream]
      });
    }
  };

  const cancelEdit = () => {
    setEditingOrg(null);
    setShowAddOrg(false);
    setOrgForm({ name: '', description: '', website: '', contact_email: '', supported_waste_streams: [], rates: [] });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Kraunama...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-green-600" />
            <span className="text-xl font-semibold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-100 rounded-lg">
              <Shield className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">Admin</span>
            </div>
            <span className="text-sm text-gray-600">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={logout}>Atsijungti</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link to="/dashboard">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Grįžti į Dashboard
            </Button>
          </Link>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Shield className="w-8 h-8 text-purple-600" />
            Admin panelis
          </h1>
          <p className="text-gray-600">
            Sistemos valdymas ir organizacijų administravimas
          </p>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Viso vartotojų
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="text-3xl font-bold">{stats.total_users}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Organizacijos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-green-600" />
                  <span className="text-3xl font-bold">{stats.total_organizations}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Skaičiavimų
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <span className="text-3xl font-bold">{stats.total_calculations}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="organizations" className="space-y-6">
          <TabsList>
            <TabsTrigger value="organizations">
              <Building2 className="w-4 h-4 mr-2" />
              Organizacijos
            </TabsTrigger>
            <TabsTrigger value="users">
              <UserCog className="w-4 h-4 mr-2" />
              Vartotojai
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="w-4 h-4 mr-2" />
              Pranešimai
            </TabsTrigger>
          </TabsList>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Atliekų tvarkymo organizacijos</CardTitle>
                    <CardDescription>
                      Valdykite organizacijas ir jų tarifus
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setShowAddOrg(!showAddOrg)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Pridėti organizaciją
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showAddOrg && (
                  <form onSubmit={handleSaveOrganization} className="mb-6 p-6 border border-green-200 bg-white rounded-lg shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4 text-lg">
                      {editingOrg ? 'Redaguoti organizaciją' : 'Nauja organizacija'}
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="org-name">Pavadinimas *</Label>
                          <Input
                            id="org-name"
                            value={orgForm.name}
                            onChange={(e) => setOrgForm({...orgForm, name: e.target.value})}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="org-email">El. paštas</Label>
                          <Input
                            id="org-email"
                            type="email"
                            value={orgForm.contact_email}
                            onChange={(e) => setOrgForm({...orgForm, contact_email: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="org-website">Svetainė</Label>
                        <Input
                          id="org-website"
                          type="url"
                          value={orgForm.website}
                          onChange={(e) => setOrgForm({...orgForm, website: e.target.value})}
                          placeholder="https://example.com"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="org-desc">Aprašymas *</Label>
                        <Textarea
                          id="org-desc"
                          value={orgForm.description}
                          onChange={(e) => setOrgForm({...orgForm, description: e.target.value})}
                          rows={3}
                          required
                        />
                      </div>

                      {/* Supported Waste Streams */}
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-3">Palaikomi atliekų srautai *</h4>
                        <p className="text-sm text-gray-500 mb-3">Pasirinkite, kokius atliekų srautus ši organizacija tvarko</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {wasteStreamOptions.map((stream) => (
                            <button
                              key={stream.value}
                              type="button"
                              onClick={() => toggleWasteStream(stream.value)}
                              className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                                orgForm.supported_waste_streams?.includes(stream.value)
                                  ? 'border-green-500 bg-green-50 text-green-700'
                                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              {stream.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Rates Section */}
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-3">Tarifai</h4>
                        
                        {orgForm.rates.length > 0 && (
                          <div className="mb-4 space-y-2">
                            {orgForm.rates.map((rate, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm">
                                  {rate.waste_stream}
                                  {rate.packaging_type && ` (${rate.packaging_type})`}: 
                                  <strong className="ml-1">€{rate.rate_per_kg}/kg</strong>
                                  <span className="text-green-600 ml-2">-{rate.discount_percentage}%</span>
                                  {rate.year && <span className="text-blue-600 ml-2 text-xs">{rate.year} m.</span>}
                                  {!rate.year && <span className="text-gray-400 ml-2 text-xs">(visi metai)</span>}
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveRate(index)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="grid md:grid-cols-5 gap-3 mb-3">
                          <Select
                            value={newRate.waste_stream}
                            onValueChange={(value) => setNewRate({...newRate, waste_stream: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="packaging">Pakuotės</SelectItem>
                              <SelectItem value="weee">WEEE</SelectItem>
                              <SelectItem value="oil">Alyvos</SelectItem>
                              <SelectItem value="textile">Tekstilė</SelectItem>
                            </SelectContent>
                          </Select>

                          {newRate.waste_stream === 'packaging' && (
                            <Select
                              value={newRate.packaging_type}
                              onValueChange={(value) => setNewRate({...newRate, packaging_type: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="plastic">Plastikas</SelectItem>
                                <SelectItem value="glass">Stiklas</SelectItem>
                                <SelectItem value="paper">Popierius</SelectItem>
                                <SelectItem value="metal">Metalas</SelectItem>
                                <SelectItem value="composite">Kombinuotos</SelectItem>
                              </SelectContent>
                            </Select>
                          )}

                          <Input
                            type="number"
                            step="0.01"
                            placeholder="€/kg"
                            value={newRate.rate_per_kg}
                            onChange={(e) => setNewRate({...newRate, rate_per_kg: e.target.value})}
                          />

                          <Input
                            type="number"
                            step="1"
                            placeholder="Nuolaida %"
                            value={newRate.discount_percentage}
                            onChange={(e) => setNewRate({...newRate, discount_percentage: e.target.value})}
                          />

                          <Input
                            type="number"
                            step="1"
                            placeholder="Metai (pvz. 2025)"
                            value={newRate.year || ''}
                            onChange={(e) => setNewRate({...newRate, year: e.target.value ? parseInt(e.target.value) : null})}
                          />
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddRate}
                          className="w-full border-dashed"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Pridėti tarifą
                        </Button>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button type="submit" className="bg-green-600 hover:bg-green-700 flex-1">
                          <Save className="w-4 h-4 mr-2" />
                          {editingOrg ? 'Išsaugoti pakeitimus' : 'Sukurti organizaciją'}
                        </Button>
                        <Button type="button" variant="outline" onClick={cancelEdit}>
                          <X className="w-4 h-4 mr-2" />
                          Atšaukti
                        </Button>
                      </div>
                    </div>
                  </form>
                )}

                <div className="space-y-4">
                  {organizations.map((org) => (
                    <div
                      key={org.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-lg mb-1">{org.name}</h4>
                          <p className="text-sm text-gray-600 mb-2">{org.description}</p>
                          {org.website && (
                            <a
                              href={org.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-green-600 hover:text-green-700"
                            >
                              {org.website}
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditOrganization(org)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteOrganization(org.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Supported Waste Streams Badges */}
                      {org.supported_waste_streams && org.supported_waste_streams.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {org.supported_waste_streams.map((stream) => (
                            <span
                              key={stream}
                              className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full"
                            >
                              {wasteStreamOptions.find(w => w.value === stream)?.label || stream}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Tarifai:</p>
                        <div className="grid md:grid-cols-2 gap-2 text-sm">
                          {org.rates?.slice(0, 4).map((rate, idx) => (
                            <div key={idx} className="bg-gray-50 p-2 rounded">
                              <span className="text-gray-600">
                                {rate.waste_stream}
                                {rate.packaging_type && ` (${rate.packaging_type})`}:
                              </span>
                              <span className="font-semibold ml-1">€{rate.rate_per_kg}/kg</span>
                              <span className="text-green-600 ml-2 text-xs">
                                (-{rate.discount_percentage}%)
                              </span>
                              {rate.year && (
                                <span className="text-blue-500 ml-1 text-xs">{rate.year} m.</span>
                              )}
                            </div>
                          ))}
                          {org.rates?.length > 4 && (
                            <div className="text-xs text-gray-500">
                              +{org.rates.length - 4} daugiau...
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Vartotojų valdymas</CardTitle>
                <CardDescription>
                  Peržiūrėkite ir valdykite sistemos vartotojus. 
                  {users.filter(u => u.approved === false).length > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">
                      <Clock className="w-3 h-3" />
                      {users.filter(u => u.approved === false).length} laukia patvirtinimo
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Pending approval section */}
                {users.filter(u => u.approved === false).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-yellow-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Laukia patvirtinimo ({users.filter(u => u.approved === false).length})
                    </h3>
                    <div className="space-y-3">
                      {users.filter(u => u.approved === false).map((u) => (
                        <div
                          key={u.id}
                          className="border-2 border-yellow-200 bg-yellow-50 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                <Clock className="w-5 h-5 text-yellow-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {u.company_name || u.email}
                                </h4>
                                <p className="text-sm text-gray-500">{u.email}</p>
                                {u.phone && <p className="text-xs text-gray-400">{u.phone}</p>}
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Registruotas: {new Date(u.created_at).toLocaleDateString('lt-LT')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 bg-yellow-200 text-yellow-800 text-xs font-bold rounded-full">
                                Laukia patvirtinimo
                              </span>
                              <Button
                                size="sm"
                                onClick={() => handleApproveUser(u.id, u.email)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Patvirtinti
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteUser(u.id, u.email)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All approved/active users */}
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                  Aktyvūs vartotojai ({users.filter(u => u.approved !== false).length})
                </h3>
                <div className="space-y-4">
                  {users.filter(u => u.approved !== false).map((u) => (
                    <div
                      key={u.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.role === 'admin' ? 'bg-purple-100' : 'bg-gray-100'}`}>
                            {u.role === 'admin' ? (
                              <Crown className="w-5 h-5 text-purple-600" />
                            ) : (
                              <Users className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {u.company_name || u.email}
                            </h4>
                            <p className="text-sm text-gray-500">{u.email}</p>
                            {u.phone && <p className="text-xs text-gray-400">{u.phone}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {u.role === 'admin' ? 'Administratorius' : 'Vartotojas'}
                          </div>
                          {u.approved === true && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Patvirtintas
                            </span>
                          )}
                          <Select
                            value={u.role}
                            onValueChange={(value) => handleUpdateUserRole(u.id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Vartotojas</SelectItem>
                              <SelectItem value="admin">Administratorius</SelectItem>
                            </SelectContent>
                          </Select>
                          {u.approved === true && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRejectUser(u.id, u.email)}
                              className="text-orange-600 hover:text-orange-700"
                              title="Atšaukti prieigą"
                              disabled={u.email === user?.email}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteUser(u.id, u.email)}
                            className="text-red-600 hover:text-red-700"
                            disabled={u.email === user?.email}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-400">
                        Registruotas: {new Date(u.created_at).toLocaleDateString('lt-LT')}
                      </div>
                    </div>
                  ))}
                  {users.filter(u => u.approved !== false).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Nėra aktyvių vartotojų
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Sukurti pranešimą</CardTitle>
                <CardDescription>
                  Išsiųskite pranešimus visiems vartotojams apie svarbius įvykius
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateNotification} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="notif-type">Pranešimo tipas</Label>
                      <Select
                        value={notificationForm.notification_type}
                        onValueChange={(value) => setNotificationForm({...notificationForm, notification_type: value})}
                      >
                        <SelectTrigger id="notif-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="system_announcement">Sistemos pranešimas</SelectItem>
                          <SelectItem value="legal_update">Teisės aktų pakeitimas</SelectItem>
                          <SelectItem value="payment_reminder">Mokėjimo priminimas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notif-priority">Prioritetas</Label>
                      <Select
                        value={notificationForm.priority}
                        onValueChange={(value) => setNotificationForm({...notificationForm, priority: value})}
                      >
                        <SelectTrigger id="notif-priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Žemas</SelectItem>
                          <SelectItem value="medium">Vidutinis</SelectItem>
                          <SelectItem value="high">Aukštas</SelectItem>
                          <SelectItem value="urgent">Skubus</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notif-title">Pavadinimas</Label>
                    <Input
                      id="notif-title"
                      value={notificationForm.title}
                      onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                      placeholder="Pvz., Nauji reikalavimai nuo 2025 m."
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notif-message">Pranešimas</Label>
                    <Textarea
                      id="notif-message"
                      value={notificationForm.message}
                      onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
                      placeholder="Įveskite pranešimo tekstą..."
                      rows={4}
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="send-email"
                      checked={notificationForm.send_email}
                      onChange={(e) => setNotificationForm({...notificationForm, send_email: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="send-email" className="cursor-pointer">
                      Siųsti el. paštu (reikalauja SendGrid konfigūracijos)
                    </Label>
                  </div>

                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    <Bell className="w-4 h-4 mr-2" />
                    Sukurti pranešimą
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
